


import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { CustomError, CustomValue } from "@Internal/Models";

import { Newsletters } from "App";
import { $t, EventBus } from "App/IOC";
import {
    Newsletter, NewsletterCount, NewsletterFields, NewsletterFrequencyKind,
    NewsletterSearch, NewsletterSendingDomain, NewsletterTag
} from "App/Models/Newsletter";

import Icon from "Web/Components/Icon.vue";
import PopupWindow from "Web/Components/Popup/Window.vue";

import DatePicker from "Web/Components/Common/Input/DatePicker.vue";
import SimpleSelect from "Web/Components/V2/Select/Simple.vue";
import TimeField from "Web/Components/Common/Input/Time.vue";

import RecipientPicker from "./RecipientPicker.vue";
import VariablePicker from "./VariablePicker.vue";

import {
    DescribeFrequency, ErrorMessage, HasInclusion, NextOccurrence, WEEKDAYS, WeekdayName
} from "./Helpers";

type IdList = "tags_in" | "tags_out" | "contacts_in" | "contacts_out";

/* « AAAA-MM-JJ » a l'heure du poste, depuis une date ISO. */
function localDate( value: string | null ): string {
    if( value === null || value === "" )
        return "";

    const date = new Date( value );

    if( isNaN( date.getTime() ) )
        return "";

    const pad = ( part: number ): string => String( part ).padStart( 2, "0" );

    return `${ date.getFullYear() }-${ pad( date.getMonth() + 1 ) }-${ pad( date.getDate() ) }`;
}

/* CREER OU REGLER UNE NEWSLETTER DEPUIS LA CONSOLE — meme fenetre que le Desktop
   ( Fred, 21/10/2026 ).

   Tout le reglage sauf l'e-mail : libelle, sujet, expediteur, reponse, rythme,
   prochain envoi, destinataires. Le blog source, la regle et le nombre d'articles
   sont des reglages du bloc Articles de l'e-mail : ils vivent dans le PB e-mail du
   client.

   L'expediteur est une adresse libre ; elle ne part en son nom que si son domaine
   est authentifie chez SendGrid ( Parametres des e-mails ), sinon l'expediteur
   par defaut la remplace. */
@Component({
    components: {
        DatePicker,
        Icon,
        PopupWindow,
        RecipientPicker,
        SimpleSelect,
        TimeField,
        VariablePicker
    }
})
export default class NewsletterForm extends Vue {

    @Prop( { required: true } )
    readonly GroupId!: number;

    @Prop( { default: null } )
    readonly Record!: Newsletter | null;

    private name: string = "";
    private subject: string = "";
    private senderName: string = "";
    private senderEmail: string = "";
    private replyTo: string = "";
    /* « Utiliser le meme e-mail pour la reponse » ( Fred, 22/10/2026, comme au
       Desktop ) : cochee, la reponse part a l'adresse de l'expediteur et son champ
       disparait. Cochee par defaut ; une newsletter dont la reponse differe se rouvre
       decochee. */
    private sameReply: boolean = true;

    private kind: NewsletterFrequencyKind = "weekly";
    private weekday: number = 1;
    private every: number = 1;
    private time: string = "09:00";
    /* Le prochain envoi, dans le selecteur de date du DS ( Fred, 21/10/2026 :
       « norme le datepicker » ). `initialNextSendAt` : la date lue, pour n'ecrire
       que si elle a change. */
    /* Le jour du prochain envoi ( « AAAA-MM-JJ » ) ; l'heure est celle du rythme. */
    private nextSendDate: string = "";
    /* Vrai des que l'utilisateur choisit lui-meme la date : le rythme ne la
       recalcule plus. */
    private nextSendEdited: boolean = false;
    private initialNextSendAt: string = "";
    private domains: NewsletterSendingDomain[] = [];
    private initialFrequency: string = "";

    private search: NewsletterSearch = {};

    private tags: NewsletterTag[] = [];

    /* Les contacts choisis, par identifiant : l'API ne rend que les numeros. */
    private contactNames: { [ id: number ]: string } = {};

    private count: NewsletterCount | null = null;
    private countBeforeExclusions: number | null = null;
    private counting: boolean = false;
    private countTimer: number = 0;

    private saving: boolean = false;

    /* Les manques ne se montrent qu'apres un clic sur Enregistrer. */
    private showErrors: boolean = false;
    private touched: string[] = [];

    private get action(): { Id: string; Name: string; Active: boolean } {
        return {
            Id: "save",
            Name: $t( this.Record === null
                ? "NEWSLETTERS_saveAndCreate"
                : "NEWSLETTERS_save" ).toString(),
            Active: this.complete && !this.saving
        };
    }

    private readonly weekdays: number[] = WEEKDAYS;
    private readonly roles: number[] = [ 1, 2, 3, 4 ];

    async mounted(): Promise<void> {
        const record = this.Record;

        if( record !== null ) {
            this.name = record.name || "";
            this.subject = record.subject || "";
            this.senderName = record.sender_name || "";
            this.senderEmail = record.sender_email || "";
            this.replyTo = record.reply_to_email || "";
            this.sameReply = this.replyTo.trim() === "" ||
                this.replyTo.trim().toLowerCase() === this.senderEmail.trim().toLowerCase();
            this.search = JSON.parse( JSON.stringify( record.search || {} ) );

            if( record.frequency !== undefined && record.frequency !== null ) {
                this.kind = record.frequency.kind;
                this.weekday = record.frequency.weekday;
                this.every = record.frequency.every;
                this.time = record.frequency.time;
            }

            this.nextSendDate = localDate( record.next_send_at );
        }

        this.initialNextSendAt = this.nextSendIso;
        this.initialFrequency = this.frequencyKey;
        this.fillNextSend();

        const [ [ tags, tagsError ], [ domains ] ] = await Promise.all( [
            Newsletters.Tags( this.GroupId ),
            Newsletters.SendingDomains( this.GroupId )
        ] );

        if( tagsError !== undefined )
            this.fail( tagsError );

        this.tags = tags || [];
        this.domains = domains || [];

        this.refreshCount();
    }

    /* TOUT EST OBLIGATOIRE POUR QUE LA NEWSLETTER PARTE ( Fred, 21/10/2026 ) :
       libelle, sujet, nom et adresse d'expediteur, adresse de reponse, au moins un
       critere de destinataires — donc aucun asterisque. Le bouton reste cliquable ;
       un clic avec un manque n'envoie rien et MONTRE chaque champ manquant ( bord
       rouge et message dessous, cadre des destinataires en rouge ). */
    /* Le message affiche sous un champ : rien tant qu'on n'a pas tente
       d'enregistrer. */
    private errorOf( field: string ): string {
        return ( this.showErrors || this.touched.indexOf( field ) >= 0 )
            ? this.missingOf( field )
            : "";
    }

    /* Un champ quitte vide passe en rouge tout de suite. */
    private touch( field: string ): void {
        if( this.touched.indexOf( field ) < 0 )
            this.touched = [ ...this.touched, field ];
    }

    private missingOf( field: string ): string {
        const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const value = ( text: string ): string => text.trim();

        switch( field ) {
            case "name":
                return value( this.name ) === "" ? this.required : "";
            case "subject":
                return value( this.subject ) === "" ? this.required : "";
            case "senderName":
                return value( this.senderName ) === "" ? this.required : "";
            case "senderEmail":
            case "replyTo": {
                /* Meme adresse : seul le champ de l'expediteur dit ce qui manque. */
                if( field === "replyTo" && this.sameReply )
                    return "";

                const text = value( field === "senderEmail" ? this.senderEmail : this.replyTo );

                if( text === "" )
                    return this.required;

                return email.test( text ) ? "" : $t( "NEWSLETTERS_emailInvalid" ).toString();
            }
            case "nextSend":
                return this.nextSendDate === "" ? this.required : "";
            case "search":
                return HasInclusion( this.search )
                    ? ""
                    : $t( "NEWSLETTERS_recipientsRequired" ).toString();
            default:
                return "";
        }
    }

    private get required(): string {
        return $t( "NEWSLETTERS_fieldRequired" ).toString();
    }

    private get complete(): boolean {
        return [ "name", "subject", "senderName", "senderEmail", "replyTo", "nextSend", "search" ]
            .every( field => this.missingOf( field ) === "" );
    }

    private get title(): string {
        return $t(
            this.Record === null ? "NEWSLETTERS_createTitle" : "NEWSLETTERS_editTitle"
        ).toString();
    }

    /* La frequence : genre et intervalle en un choix. Une valeur deja enregistree
       hors de la liste ( toutes les 6 semaines… ) y est ajoutee. */
    private get cadences(): { Id: string; Name: string }[] {
        const options = [
            [ "weekly", 1 ], [ "weekly", 2 ], [ "weekly", 3 ], [ "weekly", 4 ],
            [ "monthly_first", 1 ], [ "monthly_first", 2 ], [ "monthly_first", 3 ]
        ] as [ NewsletterFrequencyKind, number ][];

        if( !options.some( ( [ kind, every ] ) => kind === this.kind && every === this.every ) )
            options.push( [ this.kind, this.every ] );

        return options.map( ( [ kind, every ] ) => ( {
            Id: `${ kind }:${ every }`,
            Name: this.cadenceName( kind, every )
        } ) );
    }

    private cadenceName( kind: NewsletterFrequencyKind, every: number ): string {
        if( kind === "weekly" )
            return ( every === 1 )
                ? $t( "NEWSLETTERS_cadenceWeekly" ).toString()
                : $t( "NEWSLETTERS_cadenceWeeklyN", { every } ).toString();

        return ( every === 1 )
            ? $t( "NEWSLETTERS_cadenceMonthly" ).toString()
            : $t( "NEWSLETTERS_cadenceMonthlyN", { every } ).toString();
    }

    /* Les listes deroulantes du DS ( `SimpleSelect`, Fred 21/10/2026 : « norme les
       listes deroulantes » ) : des `CustomValue`, nom et identifiant. */
    private get cadenceOptions(): CustomValue<string>[] {
        return this.cadences.map( option => new CustomValue<string>( option.Name, option.Id ) );
    }

    private get selectedCadence(): CustomValue<string> | undefined {
        return this.cadenceOptions.find( option => option.Id === this.cadence );
    }

    private get weekdayOptions(): CustomValue<string>[] {
        return this.weekdays.map(
            day => new CustomValue<string>( this.dayName( day ), String( day ) )
        );
    }

    private get selectedWeekday(): CustomValue<string> | undefined {
        return this.weekdayOptions.find( option => option.Id === String( this.weekday ) );
    }

    private get cadence(): string {
        return `${ this.kind }:${ this.every }`;
    }

    private set cadence( value: string ) {
        const [ kind, every ] = value.split( ":" );

        this.kind = kind as NewsletterFrequencyKind;
        this.every = Number( every ) || 1;
    }

    private get frequencySummary(): string {
        return DescribeFrequency( {
            kind: this.kind,
            weekday: Number( this.weekday ),
            every: this.every,
            time: this.time
        } );
    }

    /* Une variable choisie s'ecrit a l'endroit du curseur dans le sujet, sinon a
       la fin. */
    private insertVariable( token: string ): void {
        const input = this.$refs.subjectInput as HTMLInputElement | undefined;
        const start = ( input !== undefined && input.selectionStart !== null )
            ? input.selectionStart
            : this.subject.length;
        const end = ( input !== undefined && input.selectionEnd !== null )
            ? input.selectionEnd
            : start;

        this.subject = this.subject.slice( 0, start ) + token + this.subject.slice( end );

        this.$nextTick( () => {
            if( input === undefined )
                return;

            input.focus();
            input.setSelectionRange( start + token.length, start + token.length );
        } );
    }

    /* Le prochain envoi en ISO, a l'heure du rythme, fuseau du poste. */
    private get nextSendIso(): string {
        if( this.nextSendDate === "" )
            return "";

        const date = new Date( `${ this.nextSendDate }T${ this.time || "09:00" }` );

        return isNaN( date.getTime() ) ? "" : date.toISOString();
    }

    private get today(): string {
        return localDate( new Date().toISOString() );
    }

    private get frequencyKey(): string {
        return [ this.kind, this.weekday, this.every, this.time ].join( "|" );
    }

    /* Une adresse d'expediteur est-elle saisie ? Sans elle, rien ne part. */
    private get senderDomainValid(): boolean | undefined {
        const email = this.senderEmail.trim();

        if( email.indexOf( "@" ) < 0 )
            return undefined;

        const domain = email.split( "@" ).pop()!.toLowerCase();

        return this.domains.some( item => item.valid && item.domain.toLowerCase() === domain );
    }

    /* Les tags offerts a une liste : pas ceux deja pris par l'autre ( un tag ne
       peut pas inclure ET exclure ). */
    private tagsFor( list: "tags_in" | "tags_out" ): NewsletterTag[] {
        const other = this.ids( list === "tags_in" ? "tags_out" : "tags_in" );

        return this.tags.filter( tag => other.indexOf( tag.id ) < 0 );
    }

    private rememberContact( id: number, label: string ): void {
        this.contactNames = { ...this.contactNames, [ id ]: label };
    }

    private roleName( role: number ): string {
        return $t( `NEWSLETTERS_role${ role }` ).toString();
    }

    private dayName( weekday: number ): string {
        return WeekdayName( weekday );
    }

    private ids( list: IdList ): number[] {
        return this.search[ list ] || [];
    }

    private setIds( list: IdList | "roles_in", values: number[] ): void {
        const search: NewsletterSearch = { ...this.search };

        if( values.length === 0 )
            delete search[ list ];
        else
            search[ list ] = values;

        this.search = search;
    }

    private hasRole( role: number ): boolean {
        return ( this.search.roles_in || [] ).indexOf( role ) >= 0;
    }

    private toggleRole( role: number, checked: boolean ): void {
        const roles = ( this.search.roles_in || [] ).filter( item => item !== role );

        this.setIds( "roles_in", checked ? [ ...roles, role ].sort() : roles );
    }

    private flag(
        name: "all_contacts" | "active_buyers" | "active_renters" | "owners_also_buyers"
    ): boolean {
        return this.search[ name ] === true;
    }

    private setFlag(
        name: "all_contacts" | "active_buyers" | "active_renters" | "owners_also_buyers",
        checked: boolean
    ): void {
        const search: NewsletterSearch = { ...this.search };

        if( checked )
            search[ name ] = true;
        else
            delete search[ name ];

        this.search = search;
    }

    /* A la creation, le prochain envoi suit le rythme tant qu'on ne l'a pas choisi
       a la main : le champ obligatoire n'est jamais vide d'office. */
    @Watch( "frequencyKey" )
    private fillNextSend(): void {
        if( this.Record !== null || this.nextSendEdited )
            return;

        const next = NextOccurrence( this.kind, this.weekday, this.time );

        this.nextSendDate = localDate( next.toISOString() );
    }

    private onNextSend( value: string | undefined ): void {
        this.nextSendDate = value || "";
        this.nextSendEdited = true;
        this.touch( "nextSend" );
    }

    @Watch( "search", { deep: true } )
    private onSearchChanged(): void {
        this.touch( "search" );
        window.clearTimeout( this.countTimer );
        this.countTimer = window.setTimeout( () => this.refreshCount(), 400 );
    }

    /* Deux comptes quand la selection retire des contacts : avec et sans les
       « Ne pas envoyer » — l'API rend `matched` APRES les retraits, il faut le
       chiffre d'avant pour dire combien ils ont enleve. */
    private async refreshCount(): Promise<void> {
        if( !HasInclusion( this.search ) ) {
            this.count = null;
            this.countBeforeExclusions = null;
            return;
        }

        this.counting = true;

        const asked = JSON.stringify( this.search );
        const withoutExclusions: NewsletterSearch = { ...this.search };

        delete withoutExclusions.tags_out;
        delete withoutExclusions.contacts_out;

        const excludes = this.hasExclusions;

        const [ [ count, error ], [ before ] ] = await Promise.all( [
            Newsletters.Count( this.GroupId, this.search ),
            excludes
                ? Newsletters.Count( this.GroupId, withoutExclusions )
                : Promise.resolve( [ undefined, undefined ] as [ undefined, undefined ] )
        ] );

        if( asked !== JSON.stringify( this.search ) )
            return;

        this.counting = false;

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.count = count || null;
        this.countBeforeExclusions = ( excludes && before !== undefined ) ? before.matched : null;
    }

    private get hasExclusions(): boolean {
        return ( this.search.tags_out || [] ).length > 0 ||
            ( this.search.contacts_out || [] ).length > 0;
    }

    /* LE CALCUL EXPLIQUE ( Fred, 21/10/2026 : « expliquer comment on a trouve ce
       resultat, retranscrire les cases cochees » ). Trois etapes, dans l'ordre
       ou l'API les applique : les contacts retenus ( criteres en OU ), ceux que
       « Ne pas envoyer » retire ( l'exclusion l'emporte toujours ), ceux ecartes
       d'office a l'envoi. */
    private get retained(): number {
        if( this.count === null )
            return 0;

        return ( this.countBeforeExclusions !== null )
            ? this.countBeforeExclusions
            : this.count.matched;
    }

    private get removed(): number {
        return ( this.count !== null && this.countBeforeExclusions !== null )
            ? Math.max( 0, this.countBeforeExclusions - this.count.matched )
            : 0;
    }

    private get inclusionText(): string {
        const search = this.search;

        if( search.all_contacts === true )
            return $t( "NEWSLETTERS_allContacts" ).toString();

        const parts: string[] = [];

        ( search.roles_in || [] ).forEach( role => parts.push( this.roleName( role ) ) );

        ( [ "active_buyers", "active_renters", "owners_also_buyers" ] as const ).forEach( flag => {
            if( search[ flag ] === true )
                parts.push( $t( `NEWSLETTERS_${ flag }` ).toString() );
        } );

        if( ( search.tags_in || [] ).length > 0 )
            parts.push( $t( "NEWSLETTERS_explainTags", {
                tags: ( search.tags_in || [] ).map( id => this.tagLabel( id ) ).join( ", " )
            } ).toString() );

        if( ( search.contacts_in || [] ).length > 0 )
            parts.push( $t( "NEWSLETTERS_explainContacts", {
                count: ( search.contacts_in || [] ).length
            } ).toString() );

        return parts.join( $t( "NEWSLETTERS_explainOr" ).toString() );
    }

    /* « Tous les contacts » coche avec d'autres criteres : ils n'ajoutent rien. */
    private get redundantInclusion(): boolean {
        const search = this.search;

        return search.all_contacts === true && (
            ( search.roles_in || [] ).length > 0 ||
            ( search.tags_in || [] ).length > 0 ||
            ( search.contacts_in || [] ).length > 0 ||
            search.active_buyers === true ||
            search.active_renters === true ||
            search.owners_also_buyers === true
        );
    }

    private get exclusionText(): string {
        const parts: string[] = [];

        if( ( this.search.tags_out || [] ).length > 0 )
            parts.push( $t( "NEWSLETTERS_explainTags", {
                tags: ( this.search.tags_out || [] ).map( id => this.tagLabel( id ) ).join( ", " )
            } ).toString() );

        if( ( this.search.contacts_out || [] ).length > 0 )
            parts.push( $t( "NEWSLETTERS_explainContacts", {
                count: ( this.search.contacts_out || [] ).length
            } ).toString() );

        return parts.join( ", " );
    }

    /* Les raisons des ecartes d'office, sans celles a zero ( Fred, 21/10/2026 ). */
    private get excludedDetails(): string {
        if( this.count === null )
            return "";

        const excluded = this.count.excluded;

        return [
            [ excluded.no_email, "NEWSLETTERS_explainNoEmail" ],
            [ excluded.not_authorized, "NEWSLETTERS_explainNotAuthorized" ],
            [ excluded.bounced, "NEWSLETTERS_explainBounced" ]
        ]
            .filter( ( [ value ] ) => Number( value ) > 0 )
            .map( ( [ value, key ] ) => $t( String( key ), { count: value } ).toString() )
            .join( ", " );
    }

    private tagLabel( id: number ): string {
        const tag = this.tags.find( item => item.id === id );

        return ( tag !== undefined ) ? `« ${ tag.name } »` : `#${ id }`;
    }

    private get excludedTotal(): number {
        if( this.count === null )
            return 0;

        const excluded = this.count.excluded;

        return ( excluded.no_email || 0 ) +
            ( excluded.not_authorized || 0 ) +
            ( excluded.bounced || 0 );
    }

    private fail( error: CustomError ): void {
        EventBus.Publish(
            "Error", new CustomError( error.Id, ErrorMessage( error.Message, error.SubId ) )
        );
    }

    private fields(): NewsletterFields {
        const fields: NewsletterFields = {
            name: this.name.trim(),
            subject: this.subject.trim(),
            sender_name: this.senderName.trim(),
            sender_email: this.senderEmail.trim(),
            reply_to_email: ( this.sameReply ? this.senderEmail : this.replyTo ).trim(),
            search: this.search
        };

        /* Le rythme ne part que s'il change ( ou a la creation ) : il recalcule le
           prochain envoi. Une date posee a la main l'emporte. */
        if( this.Record === null || this.frequencyKey !== this.initialFrequency )
            fields.frequency = {
                kind: this.kind,
                weekday: Number( this.weekday ),
                every: Math.max( 1, Math.min( 52, Math.round( Number( this.every ) || 1 ) ) ),
                time: this.time
            };

        const nextSendAt = this.nextSendIso;

        if( nextSendAt !== "" && nextSendAt !== this.initialNextSendAt )
            fields.next_send_at = nextSendAt;

        return fields;
    }

    private async save(): Promise<void> {
        if( this.saving )
            return;

        if( !this.complete ) {
            this.showErrors = true;
            return;
        }

        this.saving = true;

        const [ saved, error ] = ( this.Record === null )
            ? await Newsletters.Create( this.GroupId, this.fields() )
            : await Newsletters.Update( this.GroupId, this.Record.id, this.fields() );

        this.saving = false;

        if( error !== undefined || saved === undefined ) {
            if( error !== undefined )
                this.fail( error );

            return;
        }

        this.$emit( "Saved", saved );
    }
}
